/**
 * `link` — the busiest tier-2 type here (5,602 populated cells).
 *
 * monday's editor is a **two-field popover**, not an inline input: 320 × 200, radius 8,
 * padding 8, white, 15px/400 — the URL on top, the display text under it
 * (`discovery/ui-reference/editors/link/editor.json`, `README.md` "Two clicks open a two-field
 * popover: the URL and the display text"). The cell itself renders the display text as a
 * link, which is what monday's grid shows.
 */
import { getColumnType, type LinkSettings, type LinkValue } from '@elizmonday/shared';
import { CellPopover, useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { LinkSettingsDialog } from './SettingsDialog';

const shared = getColumnType('link');

/** Measured: the card is 320 × 200 (`editors/link/editor.json`). */
export const LINK_EDITOR_WIDTH = 320;

export const linkColumnUi = defineColumnUi<LinkValue, LinkSettings>({
  type: 'link',
  icon: 'link',
  gallery: { group: 'essentials', keywords: ['link', 'url', 'website', 'href'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    const label = value.text ?? value.url;
    return (
      <a
        className="ez-cell__link"
        href={value.url}
        target="_blank"
        rel="noreferrer noopener"
        title={value.url}
        // The grid owns the click; opening the link is the anchor's own job.
        onClick={(event) => event.stopPropagation()}
      >
        {label}
      </a>
    );
  },

  /**
   * **P-282.** The two fields are one draft, and while nobody has touched it that draft is the
   * **store's** value: a url pasted into the row while this popover is open replaces what the
   * fields show, and dismissing a popover nobody touched writes nothing at all. Before this,
   * pasting into an open link editor wrote the url and then wrote `null` back over it on the
   * dismiss — the defect the card is filed on. `useEditorDraft` carries the rule for every
   * popover type; nothing here is `link`-specific.
   */
  Editor: ({ value, initialInput, onCommit, onCancel }) => {
    const stored = {
      url: value?.url ?? '',
      // monday leaves the display field empty when the text *is* the url.
      text: value === null || value.text === value.url ? '' : (value.text ?? ''),
    };
    const editor = useEditorDraft(
      stored,
      initialInput === undefined ? undefined : { url: initialInput, text: '' },
    );
    const { url, text } = editor.draft;
    const setUrl = (next: string): void => editor.set({ url: next, text });
    const setText = (next: string): void => editor.set({ url, text: next });
    const commit = (): void => {
      const trimmed = url.trim();
      if (trimmed === '') {
        onCommit(null);
        return;
      }
      onCommit({ url: trimmed, text: text.trim() === '' ? trimmed : text.trim() });
    };
    const dismiss = (): void => editor.dismiss(commit, onCancel);
    return (
      <CellPopover label="Edit link" width={LINK_EDITOR_WIDTH} onDismiss={dismiss} onCancel={onCancel}>
        <div className="ez-two-field">
          <label className="ez-two-field__label" htmlFor="ez-link-url">
            Link
          </label>
          <input
            id="ez-link-url"
            className="ez-two-field__input"
            data-testid="link-url"
            autoFocus
            value={url}
            placeholder="https://"
            onChange={(event) => setUrl(event.target.value)}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') commit();
              if (event.key === 'Escape') onCancel();
            }}
          />
          <label className="ez-two-field__label" htmlFor="ez-link-text">
            Text to display
          </label>
          <input
            id="ez-link-text"
            className="ez-two-field__input"
            data-testid="link-label"
            value={text}
            placeholder="Optional"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') commit();
              if (event.key === 'Escape') onCancel();
            }}
          />
        </div>
      </CellPopover>
    );
  },

  /** `"<text> - <url>"` is what `toText` produces, so the inverse splits on the same marker. */
  SettingsDialog: LinkSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const separator = trimmed.indexOf(' - ');
    if (separator > 0) {
      const label = trimmed.slice(0, separator);
      const url = trimmed.slice(separator + 3);
      return shared.parseValue({ url, text: label }, settings) as LinkValue | null;
    }
    return shared.parseValue({ url: trimmed }, settings) as LinkValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('link'),
});
